import React, { useEffect, useState } from "react";
import { Box, Typography, TextField, Button } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbar,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { useSelector } from "react-redux";
import ReportItemDashboard from "./ItemDashboardReport";
import { LinearProgress, Fade } from "@mui/material";

function CustomToolbar({ onRefresh }) {
  return (
    <GridToolbarContainer>
      <GridToolbar />
      <Button
        onClick={onRefresh}
        color="primary"
        variant="outlined"
        startIcon={<RefreshIcon />}
        sx={{ ml: 1 }}
      >
        Refresh
      </Button>
    </GridToolbarContainer>
  );
}

function DashboardReport() {
  const userState = useSelector((state) => state.user);
  const apiRef = useGridApiRef();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [dateEvenementSearch, setDateEvenementSearch] = useState("");
  const [dateCompleteSearch, setDateCompleteSearch] = useState("");

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Report/GetCompletedReports/${userState.username}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        },
      );
      if (!response.ok) throw new Error("Erreur API");
      const data = await response.json();
      setRows(data.reports.map((r) => ({ id: r.idActivite, ...r })));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [userState]);

  const filteredRows = rows.filter(
    (row) =>
      [
        row.titleRepportFr,
        row.titleRepportEn,
        row.username,
        row.nomEmplacement,
      ].some((field) =>
        field?.toString().toLowerCase().includes(searchText.toLowerCase()),
      ) &&
      row.nomEmplacement
        ?.toLowerCase()
        .includes(locationSearch.toLowerCase()) &&
      (dateEvenementSearch
        ? row.dateEvenement?.startsWith(dateEvenementSearch)
        : true) &&
      (dateCompleteSearch
        ? row.dateComplete?.startsWith(dateCompleteSearch)
        : true),
  );

  const handleResetFilters = () => {
    setSearchText("");
    setLocationSearch("");
    setDateEvenementSearch("");
    setDateCompleteSearch("");
    if (apiRef.current) {
      apiRef.current.setFilterModel({
        items: [
          { columnField: "username", operatorValue: "contains", value: "" },
        ],
      });
    }
  };

  const handleRefresh = async () => {
    handleResetFilters();
    await fetchReports();
  };

  const formatDateByLang = (value) => {
    if (!value) return "";
    const date = new Date(value);
    const locale = userState.lang === "FR" ? "fr-FR" : "en-US";
    return date.toLocaleDateString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const columns = [
    { field: "idActivite", headerName: "Id", flex: 1 },
    { field: "idTypeActivite", hide: true, filterable: false },
    { field: "titleRepportFr", headerName: "Titre (FR)", flex: 1 },
    { field: "titleRepportEn", headerName: "Titre (EN)", flex: 1 },
    { field: "username", headerName: "Utilisateur", flex: 1 },
    { field: "nomEmplacement", headerName: "Emplacement", flex: 1 },
    {
      field: "dateEvenement",
      headerName: "Date événement",
      type: "dateTime",
      flex: 1,
      valueGetter: (value) => (value ? new Date(value) : null),
      valueFormatter: (value) => formatDateByLang(value),
    },
    {
      field: "dateComplete",
      headerName: "Date complétion",
      type: "dateTime",
      flex: 1,
      valueGetter: (value) => (value ? new Date(value) : null),
      valueFormatter: (value) => formatDateByLang(value),
    },
    {
      field: "details",
      headerName: "Rapport",
      sortable: false,
      filterable: false,
      width: 650, // largeur plus grande pour voir boutons
      cellClassName: "report-action-cell",
      renderCell: (params) => (
        <ReportItemDashboard
          report={params.row}
          typeActivite={
            userState.lang === "EN"
              ? params.row.titleRepportEn
              : params.row.titleRepportFr
          }
          nomEmplacement={params.row.nomEmplacement}
          username={params.row.username}
          date={params.row.dateComplete}
          dateEvent={params.row.dateEvenement}
          setReloadReports={fetchReports} // reload complet après Reopen
        />
      ),
    },
  ];

  return (
    <Box sx={{ height: 700, width: "100%", p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Lignes affichées : {filteredRows.length} / {rows.length}
      </Typography>

      <Box
        sx={{
          mb: 2,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr 1fr auto" },
          gap: 2,
          alignItems: "center",
        }}
      >
        <TextField
          label="Recherche rapide"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          fullWidth
        />
        <TextField
          label="Filtrer par emplacement"
          value={locationSearch}
          onChange={(e) => setLocationSearch(e.target.value)}
          fullWidth
        />
        <TextField
          label="Filtrer par date événement (yyyy-mm-dd)"
          value={dateEvenementSearch}
          onChange={(e) => setDateEvenementSearch(e.target.value)}
          fullWidth
        />
        <TextField
          label="Filtrer par date complétion (yyyy-mm-dd)"
          value={dateCompleteSearch}
          onChange={(e) => setDateCompleteSearch(e.target.value)}
          fullWidth
        />
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<RestartAltIcon />}
          onClick={handleResetFilters}
          disabled={
            !searchText &&
            !locationSearch &&
            !dateEvenementSearch &&
            !dateCompleteSearch
          }
        >
          Réinitialiser
        </Button>
      </Box>
      <Fade in={loading} unmountOnExit>
        <Box sx={{ width: "100%", mb: 1 }}>
          <LinearProgress
            color="primary"
            sx={{
              height: 6,
              borderRadius: 3,
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
              },
            }}
          />
        </Box>
      </Fade>

      <DataGridPro
        sx={{
          "& .report-action-cell:focus": {
            outline: "none",
          },
          "& .report-action-cell:focus-within": {
            outline: "none",
          },
        }}
        apiRef={apiRef}
        rows={filteredRows}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        getRowHeight={() => "auto"}
        slots={{ toolbar: CustomToolbar }}
        slotProps={{ toolbar: { onRefresh: handleRefresh } }}
        pagination={false}
        initialState={{
          filter: {
            filterModel: {
              items: [
                {
                  columnField: "username",
                  operatorValue: "contains",
                  value: "",
                },
              ],
            },
          },
          columns: { columnVisibilityModel: { idTypeActivite: false } },
        }}
      />
    </Box>
  );
}

export default DashboardReport;
